import ThemeToggleBt from '@/shared/components/ThemBtn/ThemeToggleBtn';
import { projectConfig } from '@/shared/configs/projectConfig';
import { AuthContext } from '@/shared/context/AuthContext';
import logo from '@/shared/images/storm_icon-icons.com_67503.png';
import { classNames, generateAvatarColor, PROFILE_ROUTE, RELEASE_NOTES_ROUTE } from '@/shared/utils';
import { LogoutOutlined, RightOutlined } from '@ant-design/icons';
import { Tooltip, Typography } from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import styles from './DrawerProfile.module.css';
import NavItem from './components/NavItem/NavItem';
import { useTour } from './useTour';
import { TourComponent } from '@/shared/components/TourComponent/TourComponent';

const DrawerProfile = ({ menuItems }) => {
  const location = useLocation();
  const auth = useContext(AuthContext);
  const userInfo = useSelector(state => state.userInfo.userState);
  const isAdmin = useSelector(state => state.userInfo.isAdmin);
  const [open, setOpen] = useState(false);
  const { Title, Text } = Typography;
  const drawerRef = useRef(null);
  const navigate = useNavigate();
  const { changeThemeButtonRef, profileRef, menuRef, logoRef, exitButtonRef, steps } = useTour(isAdmin);
  const [isTourOpen, setIsTourOpen] = useState(false);

  const isActive = path => matchPath({ path, end: true }, location.pathname);

  const toggleDrawer = () => setOpen(prev => !prev);

  const onExit = async () => {
    try {
      auth.logout();
      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        const toggleButton = document.querySelector(`.${styles.toggleOpen}`);
        if (toggleButton && !toggleButton.contains(event.target)) {
          setOpen(false);
        }
      }
    };

    if (open && !isTourOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, isTourOpen]);

  return (
    <>
      <div ref={drawerRef} className={classNames(styles.drawer, { [styles.opened]: open })}>
        <div className={styles.topBox}>
          <div className={styles.drawerHeader}>
            <div className={styles.topLeftBox}>
              <div className={styles.themeButtonWrapper} ref={changeThemeButtonRef}>
                <ThemeToggleBt />
              </div>
              <TourComponent
                handleOpen={() => setIsTourOpen(true)}
                handleClose={() => setIsTourOpen(false)}
                isTourOpen={isTourOpen}
                className={styles.tourButton}
                steps={steps}
              />
            </div>
            <button className={styles.toggleOpen} onClick={toggleDrawer}>
              <RightOutlined className={classNames(styles.toggleOpenIcon, { [styles.toggleOpenIconOpened]: open })} />
            </button>
          </div>
          <div
            className={classNames(styles.userInformation, { [styles.userInformationOpened]: open })}
            onClick={() => navigate(PROFILE_ROUTE)}
            ref={profileRef}
          >
            <div className={classNames(styles.userInfoBox, { [styles.userInfoBoxOpened]: open })}>
              <Tooltip placement='topLeft' title={userInfo?.name}>
                <Title level={4} className={styles.userName}>
                  {userInfo?.name}
                </Title>
              </Tooltip>
              <Text type='secondary'>{userInfo.sub_id === 0 ? 'Админ' : 'Пользователь'}</Text>
            </div>
            {userInfo.avatar ? (
              <div
                style={{ backgroundImage: `url(/uploads/avatars/${userInfo.avatar})` }}
                className={classNames(styles.avatar, { [styles.avatarOpened]: open })}
              />
            ) : (
              <div
                className={classNames(styles.avatar, { [styles.avatarOpened]: open })}
                style={{ backgroundColor: generateAvatarColor(userInfo.name) }}
              >
                {userInfo?.name && userInfo?.name?.[0]}
              </div>
            )}
          </div>
          <div className={styles.menuWrapper} ref={menuRef}>
            {menuItems.map(item => (
              <NavItem key={item.key} item={item} isActive={isActive(item.link)} open={open} />
            ))}
          </div>
        </div>
        <div className={styles.footerContainer}>
          <div
            className={classNames(
              styles.stormLogo,
              { [styles.stormLogoOpened]: open },
              { [styles.stormLogoActive]: isAdmin }
            )}
            ref={logoRef}
            onClick={() => isAdmin && navigate(RELEASE_NOTES_ROUTE)}
          >
            <img
              src={logo}
              alt='logo'
              className={classNames(styles.stormLogoImg, { [styles.stormLogoImgOpened]: open })}
            />
            <div className={styles.informationBox}>
              <Title level={4} style={{ marginBottom: 0 }}>
                STORM
              </Title>
              <Text type='secondary'>version {projectConfig.CURRENT_VERSION}</Text>
            </div>
          </div>
          <div className={styles.drawerFooter}>
            <button className={styles.logoutButton} onClick={onExit} ref={exitButtonRef}>
              <div className={classNames(styles.navBarIcon, { [styles.navBarIconOpened]: open })}>
                <LogoutOutlined className={styles.logoutIcon} />
              </div>
              <span className={classNames(styles.textContainer, { [styles.textContainerOpened]: open })}>
                <p className={styles.logoutText}>Выйти из системы</p>
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DrawerProfile;
